/**
 * Table export utilities for extracting data from HTML tables
 * and converting to CSV / Markdown formats.
 */

export function extractTableData(tableEl: HTMLTableElement): {
  headers: string[]
  rows: string[][]
} {
  const headers = Array.from(tableEl.querySelectorAll("thead th")).map((th) =>
    (th.textContent ?? "").trim()
  )

  const rows = Array.from(tableEl.querySelectorAll("tbody tr"))
    .map((tr) =>
      Array.from(tr.querySelectorAll("td")).map((td) =>
        (td.textContent ?? "").trim()
      )
    )
    .filter((row) => row.length > 0)

  return { headers, rows }
}

function escapeCSVValue(value: string): string {
  if (
    value.includes(",") ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

export function generateCSV(headers: string[], rows: string[][]): string {
  const headerLine = headers.map(escapeCSVValue).join(",")
  const dataLines = rows.map((row) => row.map(escapeCSVValue).join(","))
  return "\uFEFF" + [headerLine, ...dataLines].join("\n")
}

export function generateMarkdown(headers: string[], rows: string[][]): string {
  const escape = (v: string) => v.replace(/\|/g, "\\|")
  const headerLine = `| ${headers.map(escape).join(" | ")} |`
  const separatorLine = `| ${headers.map(() => "---").join(" | ")} |`
  const dataLines = rows.map(
    (row) => `| ${row.map(escape).join(" | ")} |`
  )
  return [headerLine, separatorLine, ...dataLines].join("\n")
}

/**
 * Trigger a browser file download for a binary blob. The canonical helper
 * for both string-content exports (see `downloadFile`) and pre-built Blob
 * payloads (e.g. server-streamed `.xlsx`).
 *
 * @param filename - Suggested filename for the download dialog.
 * @param blob - The binary payload.
 */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function downloadFile(
  content: string,
  filename: string,
  mimeType: string
): void {
  downloadBlob(filename, new Blob([content], { type: mimeType }))
}
